import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { BasePopupComponent } from '../model/base-popup.model';
import { TranslateService } from '@ngx-translate/core';
import { MainMapService } from '../../../../shared/service/main-map.service';
import { CONSTANTS_VALUE } from '../../../../config/constants.module';
import { SelectionService } from '../../../../shared/service/selection.service';
import { LayerInfosService } from '../../../../shared/service/layer-infos.service';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { CodedValueDomain } from '../../../../bottom-panel/models/attribute-config.model';

@Component({
  selector: 'identify-popup',
  templateUrl: './identify-popup.component.html',
  standalone: false,
})
export class IdentifyPopupComponent extends BasePopupComponent implements AfterViewInit, OnDestroy {
  @ViewChild('popupRef', { static: true }) popupRef: ElementRef;
  // Reference to this component object
  public identifiedFeatures: any;

  private destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private mainMapService: MainMapService,
    private selectionService: SelectionService,
    private layerInfosService: LayerInfosService
  ) {
    super();
  }

  public init(event: any, res: any, popupTitle: string) {
    super.setBaseProperties(
      event,
      this.mainMapService.mapView,
      this.popupRef,
      false,
      this.translateService,
      false,
      popupTitle,
      false
    );
    this.setIdentifiedFeatures(res.results);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * @function setIdentifiedFeatures
   * @description Set identified Features into component for
   * popup list
   * @param identifyResults
   */
  private setIdentifiedFeatures(identifyResults: any) {
    this.processIdentifiedFeatures(identifyResults).pipe(takeUntil(this.destroy$)).subscribe(
      updatedFeatures => {
        this.identifiedFeatures = updatedFeatures;
        this.cdr.detectChanges();
      },
      error => {
        console.error('setIdentifiedFeatures: Error processing identified features:', error);
      }
    );
  }

  private processIdentifiedFeatures(features: any[]): Observable<any[]> {
    if (!features || features.length === 0) {
      return of([]);
    }
    const featureObservables: Observable<any>[] = features.map(result => {
      return this.createFeatureIdentifyInfo(result).pipe(
        map(displayIdentifyInfo => {
          result.displayIdentifyInfo = displayIdentifyInfo;
          return result;
        })
      );
    });
    return forkJoin(featureObservables);
  }

  private createFeatureIdentifyInfo(result: any): Observable<string> {
    if (
      !result ||
      !result.popUpInfo ||
      !result.popUpInfo.displayField ||
      !result.result ||
      !result.result.feature ||
      !result.result.feature.attributes
    ) {
      return of('');
    }

    const attributes = result.result.feature.attributes;
    const displayFields = result.popUpInfo.displayField;

    return this.layerInfosService.getLayerInfos(result.result.featureLayerUrl).pipe(
      map(layerInfos => {
        return displayFields
          .map((field: string) => {
            const identifyField = layerInfos.data.fields.find((f: __esri.Field) => f.name === field);

            if (identifyField && identifyField.domain && identifyField.domain.codedValues) {
              const codedValue = identifyField.domain.codedValues.find(
                (cv: CodedValueDomain) => cv.code === attributes[field]
              );
              const value = codedValue ? codedValue.name : attributes[field];
              return value !== null && value !== undefined ? String(value) : '';
            } else {
              const value = attributes[field];
              return value !== null && value !== undefined ? String(value) : '';
            }
          })
          .join('-');
      })
    );
  }

  /**
   * @name rowClick
   * @description Sends row-click event
   * @param result
   */
  public rowClick(result: any) {
    this.sendResultToDetailView(result.result);
    this.mainMapService.mapView.closePopup();
  }

  /**
   * @name rowEnter
   * @param result Selected row
   * @description Sends row-enter event
   */
  public rowEnter(result: any) {
    this.selectionService.addSelectedGraphic(result.result.feature);
  }

  /**
   * @name rowLeave
   * @param result Selected row
   * @description Sends row-leave event
   */
  public rowLeave(result: any) {
    this.selectionService.removeSelectedGraphic(result.result.feature);
  }

  /**
   * @name sendResultToDetailView
   * @param result
   * @description sends the result to the detail view
   */
  private sendResultToDetailView(result: any) {
    this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.wait);
    this.selectionService.removeAllSelectedGraphics();
    this.selectionService.addSelectedGraphic(result.feature);
    const layerName = result.editableLayers[0].fkLayer;
    this.mainMapService.handleNavigationByDetailView(layerName, result);
  }

  ngAfterViewInit(): void {
    this.updateZoomButtonTitles();
  }

  updateZoomButtonTitles() {
    setTimeout(() => {
      const zoomInButton = document.querySelector('.esri-zoom .esri-widget--button[title="Vergrößern"]') as HTMLElement;
      const zoomOutButton = document.querySelector(
        '.esri-zoom .esri-widget--button[title="Verkleinern"]'
      ) as HTMLElement;

      if (zoomInButton) {
        zoomInButton.setAttribute('title', this.translateService.instant('WIDGET.ZOOM.IN'));
      }
      if (zoomOutButton) {
        zoomOutButton.setAttribute('title', this.translateService.instant('WIDGET.ZOOM.OUT'));
      }
    }, 500); // Pequeño delay para asegurarse de que los botones estén disponibles
  }
}
